import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Eye, Gauge, Plus, Search, Table2 } from "lucide-react";
import {
  api,
  type DataEndpointRateLimit,
  type DatabaseTablesReport,
  type Schema,
} from "@/lib/api";
import { ROUTES } from "@/routes";
import {
  normalizeDataEndpointRateLimit,
  summarizeOverrides,
} from "@/lib/endpointRateLimit";
import { EmptyState, ErrorBox, PageHeader, Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type EntityType = "all" | "table" | "view";

export function TablesPage() {
  const { id = "" } = useParams<{ id: string }>();
  const location = useLocation();
  const databaseTablesPath = ROUTES.databaseTables(id);
  const isDatabaseTablesRoute =
    location.pathname === databaseTablesPath ||
    location.pathname.startsWith(`${databaseTablesPath}/`);
  const tableRoot = isDatabaseTablesRoute
    ? databaseTablesPath
    : ROUTES.tables(id);
  const [schema, setSchema] = useState<Schema | null>(null);
  const [rateLimits, setRateLimits] = useState<DataEndpointRateLimit[]>([]);
  const [tableStats, setTableStats] = useState<DatabaseTablesReport["tables"]>([]);
  const [search, setSearch] = useState("");
  const [entityType, setEntityType] = useState<EntityType>("all");
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const [schemaRes, rlRes, statsRes] = await Promise.all([
          api.getSchema(id),
          api
            .listDataEndpointRateLimits(id)
            .catch(() => ({ data: [] as DataEndpointRateLimit[] })),
          api.databaseTables(id).catch(() => null),
        ]);
        setSchema({
          ...schemaRes.data,
          tables: schemaRes.data.tables ?? [],
          views: schemaRes.data.views ?? [],
        });
        setRateLimits(
          (rlRes.data ?? []).map((row) => normalizeDataEndpointRateLimit(row)),
        );
        setTableStats(statsRes?.data.tables ?? []);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const overrideByTable = useMemo(() => {
    const map = new Map<string, DataEndpointRateLimit>();
    for (const item of rateLimits) {
      if (!item.using_defaults) map.set(item.table_name, item);
    }
    return map;
  }, [rateLimits]);

  const overrideSummary = useMemo(
    () => summarizeOverrides(rateLimits),
    [rateLimits],
  );
  const statsByTable = useMemo(
    () => new Map(tableStats.map((row) => [row.table_name, row])),
    [tableStats],
  );

  const filteredTables = useMemo(() => {
    if (!schema || (entityType !== "all" && entityType !== "table")) return [];
    const query = search.trim().toLowerCase();
    return schema.tables.filter((table) =>
      !query ||
      table.name.toLowerCase().includes(query) ||
      (table.description ?? "").toLowerCase().includes(query),
    );
  }, [entityType, schema, search]);

  const filteredViews = useMemo(() => {
    if (!schema || (entityType !== "all" && entityType !== "view")) return [];
    const query = search.trim().toLowerCase();
    return schema.views.filter((view) => view.name.toLowerCase().includes(query));
  }, [entityType, schema, search]);

  if (loading && !schema) return <Spinner label="Loading tables…" />;

  const tableCount = schema?.tables.length ?? 0;
  const viewCount = schema?.views.length ?? 0;
  const visibleCount = filteredTables.length + filteredViews.length;
  const schemaName = schema?.schema_name || "public";

  return (
    <div>
      <PageHeader
        eyebrow="Database"
        title="Database Tables"
        description="Browse tables and views in your project schema. Search entities, inspect columns, and open a table to work with its rows."
        actions={
          <Button variant="default" size="sm" asChild>
            <Link to={ROUTES.sql(id)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              New table
            </Link>
          </Button>
        }
      />

      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <Card className="mb-5" data-testid="database-table-filters">
        <CardContent className="grid gap-4 p-4 md:grid-cols-[minmax(10rem,0.65fr)_minmax(16rem,1.5fr)_minmax(10rem,0.65fr)] md:items-end">
          <div>
            <label className="label" htmlFor="database-table-schema">
              Schema
            </label>
            <select
              id="database-table-schema"
              className="input"
              value={schemaName}
              onChange={() => undefined}
              aria-label="Schema"
            >
              <option value={schemaName}>{schemaName}</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="database-table-search">
              Search for a table
            </label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
                aria-hidden
              />
              <input
                id="database-table-search"
                className="input pl-9"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search for a table"
                type="search"
              />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="database-entity-type">
              Entity Type
            </label>
            <select
              id="database-entity-type"
              className="input"
              value={entityType}
              onChange={(event) => setEntityType(event.target.value as EntityType)}
            >
              <option value="all">All entities</option>
              <option value="table">Tables</option>
              <option value="view">Views</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="tabular-nums">
          {visibleCount} shown
        </Badge>
        <Badge variant="secondary" className="tabular-nums">
          {tableCount} table{tableCount !== 1 ? "s" : ""}
        </Badge>
        <Badge variant="secondary" className="tabular-nums">
          {viewCount} view{viewCount !== 1 ? "s" : ""}
        </Badge>
        {overrideSummary.length > 0 ? (
          <Badge
            variant="default"
            className="tabular-nums"
            data-testid="tables-rate-limit-count"
          >
            {overrideSummary.length} custom rate limit
            {overrideSummary.length !== 1 ? "s" : ""}
          </Badge>
        ) : null}
      </div>

      {overrideSummary.length > 0 ? (
        <Card className="mb-5" data-testid="tables-rate-limit-summary">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 font-sans text-base">
              <Gauge className="h-4 w-4 text-secondary" aria-hidden />
              Custom endpoint rate limits
            </CardTitle>
            <CardDescription>
              Tables with overrides on <code className="font-mono text-xs">/v1/data/:table</code>.
              Open a table to edit.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border-standard/70 text-sm">
              {overrideSummary.map((row) => (
                <li
                  key={row.table}
                  className="flex flex-wrap items-center justify-between gap-2 py-2 first:pt-0 last:pb-0"
                >
                  <Link
                    className="font-mono font-medium text-secondary hover:underline"
                    to={`${tableRoot}/${encodeURIComponent(row.table)}`}
                  >
                    {row.table}
                  </Link>
                  <span className="text-xs text-text-muted">{row.summary}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {!schema || visibleCount === 0 ? (
        <EmptyState
          title={schema && (search || entityType !== "all") ? "No matching entities" : "No tables or views yet"}
          icon={<Table2 className="h-5 w-5" />}
        >
          {schema && (search || entityType !== "all")
            ? "Try a different search or entity type."
            : "Create one from the SQL editor in migration mode."}
        </EmptyState>
      ) : (
        <Card data-testid="database-tables-list">
          <CardHeader className="border-b border-border-standard pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="font-sans text-base">Entities</CardTitle>
                <CardDescription>Schema objects available to this project.</CardDescription>
              </div>
              <span className="font-mono text-xs text-text-muted">{schemaName}</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="table-wrap">
              <table className="data min-w-[860px]">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th className="text-right">Rows (Estimated)</th>
                    <th className="text-right">Columns</th>
                    <th className="text-right">Size (Estimated)</th>
                    <th>Realtime</th>
                    <th>RLS</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredTables.map((table) => {
                    const stats = statsByTable.get(table.name);
                    const rateLimit = overrideByTable.get(table.name);
                    return (
                      <tr key={`table-${table.name}`}>
                        <td>
                          <Link
                            className="inline-flex items-center gap-2 font-mono text-sm font-semibold text-secondary hover:underline"
                            to={`${tableRoot}/${encodeURIComponent(table.name)}`}
                          >
                            <Table2 className="h-3.5 w-3.5" aria-hidden />
                            {table.name}
                          </Link>
                          {rateLimit ? (
                            <Badge variant="default" className="ml-2 text-[10px]">
                              Rate limit
                            </Badge>
                          ) : null}
                        </td>
                        <td className="max-w-[22rem] text-text-muted">
                          {table.description || "—"}
                        </td>
                        <td className="text-right font-mono tabular-nums">
                          {stats ? (stats.n_live_tup ?? stats.estimated_rows).toLocaleString() : "—"}
                        </td>
                        <td className="text-right font-mono tabular-nums">{table.columns.length}</td>
                        <td className="text-right font-mono tabular-nums">
                          {stats ? formatBytes(stats.total_bytes) : "—"}
                        </td>
                        <td>
                          <Badge
                            variant="success"
                            title="Realtime subscriptions are available for project tables"
                          >
                            Enabled
                          </Badge>
                        </td>
                        <td>
                          {typeof table.rls_enabled === "boolean" ? (
                            <Badge variant={table.rls_enabled ? "success" : "secondary"}>
                              {table.rls_enabled ? "Enabled" : "Disabled"}
                            </Badge>
                          ) : (
                            <span className="text-text-disabled">—</span>
                          )}
                        </td>
                        <td className="text-right">
                          <Link
                            className="whitespace-nowrap text-xs font-semibold text-secondary hover:underline"
                            to={`${tableRoot}/${encodeURIComponent(table.name)}`}
                          >
                            View columns
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredViews.map((view) => (
                    <tr key={`view-${view.name}`}>
                      <td>
                        <Link
                          className="inline-flex items-center gap-2 font-mono text-sm font-semibold text-secondary hover:underline"
                          to={ROUTES.view(id, view.name)}
                        >
                          <Eye className="h-3.5 w-3.5" aria-hidden />
                          {view.name}
                        </Link>
                      </td>
                      <td className="text-text-muted">Read-only view</td>
                      <td className="text-right font-mono tabular-nums">—</td>
                      <td className="text-right font-mono tabular-nums">{view.columns.length}</td>
                      <td className="text-right font-mono tabular-nums">—</td>
                      <td>
                        <Badge variant="secondary" title="Views are read-only in Realtime">
                          Disabled
                        </Badge>
                      </td>
                      <td><span className="text-text-disabled">—</span></td>
                      <td className="text-right">
                        <Link
                          className="whitespace-nowrap text-xs font-semibold text-secondary hover:underline"
                          to={ROUTES.view(id, view.name)}
                        >
                          View columns
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value < 1024) return `${Math.round(value)} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KiB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GiB`;
}
